import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import { CaretIcon } from '@rsos/assets/icons';
import { Select, Text } from '@rsos/components/capstone/base';
import { ChevronContainer as ChevronContainerBase } from '@rsos/components/capstone/base/Select/Select.styles';
import { setPureWirelessFloor } from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { SpatialLayerCardContainer } from './MapCard.styles';

const ChevronContainer = ChevronContainerBase as React.ComponentType<
  React.HTMLAttributes<HTMLDivElement>
>;

// Styled components
const CardContainer = styled.div`
  background: ${({ theme }) => theme.colors.background};
`;

const HeaderSection = styled.div`
  padding: 12px 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
`;

const BorderedSection = styled.div`
  padding: 9px 13px;
  border: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
  margin: 9px 13px 0 13px;
  border-radius: 4px;
`;

const FloorSelectorSection = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.heavyLineOutline};
  margin: 9px 13px;
  border-radius: 4px;
  padding-bottom: 9px;
`;

const PropertiesContainer = styled.div`
  padding: 9px 13px;
`;

const PropertyRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
`;

const FloorSelectorContainer = styled.div`
  padding: 9px 13px 0 13px;
`;

// Portal the select menu to body so it's not hidden by overflow
const PORTAL_TARGET = document.querySelector('body');

// TypeScript interfaces
interface FloorOption {
  value: string;
  label: string;
  isSelected: boolean;
  data: {
    floor_level: number;
    floor_label: string;
  };
}

interface PureWirelessMapCardData {
  layerData: {
    source: string;
    title?: string;
  };
  spatialObject: {
    properties: {
      site_name?: string;
      building?: string;
      street_address?: string;
      postal_community?: string;
      zip?: string;
      floor_level?: number;
      floor_label?: string;
      space_type?: string;
      room_label?: string;
      label_id?: string;
      room_ID?: string;
      wing?: string;
    };
    floors?: {
      floor_level: number;
      floor_label: string;
    }[];
  };
  selectedFloor?: number | null;
  estimatedCallFloor?: number | null;
}

interface RootState {
  mapCard: {
    mapCardData: PureWirelessMapCardData | null;
  };
}

// Dropdown indicator component matching GeoComm style
const DropdownIndicator = (props: {
  selectProps: { isLoading?: boolean; menuIsOpen?: boolean };
  isDisabled?: boolean;
}) => {
  return (
    <ChevronContainer
      style={{
        ...(props.selectProps.isLoading ? { display: 'none' } : {}),
        ...(props.isDisabled ? { cursor: 'not-allowed' } : {}),
        transform: `rotate(${props.selectProps.menuIsOpen ? -180 : 0}deg)`,
      }}
      data-name="select-arrow"
    >
      <CaretIcon width={7} height={11} />
    </ChevronContainer>
  );
};

const customComponents = {
  DropdownIndicator: DropdownIndicator,
};

interface FloorSelectorProps {
  theme: DefaultTheme & { colors: Record<string, string> };
  floorOptions: FloorOption[];
  selectedFloorIndex: number;
  onFloorChange: (option: FloorOption) => void;
  isDisabled: boolean;
}

// Floor selector styled like GeoCommFloorSelector
const FloorSelectorInner = ({
  theme,
  floorOptions,
  selectedFloorIndex,
  onFloorChange,
  isDisabled,
}: FloorSelectorProps) => {
  const selectStyles = {
    clearIndicator: (styles: Record<string, unknown>) => ({
      ...styles,
      display: 'none',
    }),
    container: (styles: Record<string, unknown>) => ({
      color: theme.colors.primaryText,
      ...styles,
      height: 36,
      pointerEvents: 'auto' as const,
      width: '100%',
      zIndex: 555,
    }),
    control: (styles: Record<string, unknown>) => ({
      ...styles,
      borderColor: theme.colors.heavyLineOutline,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      maxHeight: 36,
      minHeight: 32,
      width: '100%',
    }),
    indicatorsContainer: (styles: Record<string, unknown>) => ({
      ...styles,
      cursor: 'inherit',
      height: 36,
      width: 30,
    }),
    menu: (styles: Record<string, unknown>) => ({
      ...styles,
      cursor: 'pointer',
    }),
    menuList: (styles: Record<string, unknown>) => ({
      ...styles,
      cursor: 'pointer',
    }),
    option: (styles: Record<string, unknown>) => ({
      ...styles,
      background: theme.colors.background,
      color: theme.colors.primaryText,
      cursor: 'pointer',
      fontSize: '14px',
    }),
    valueContainer: (styles: Record<string, unknown>) => ({
      ...styles,
      height: 36,
    }),
  };

  return (
    <FloorSelectorContainer>
      <Text color="secondaryText" mb="9px">
        {floorOptions.length > 0
          ? selectedFloorIndex >= 0
            ? `Floor ${selectedFloorIndex + 1} of ${floorOptions.length} selected`
            : 'No floor selected'
          : 'No Indoor Map Data Available'}
      </Text>

      <Select
        captureMenuScroll={true}
        components={customComponents}
        isDisabled={isDisabled || floorOptions.length === 0}
        isSearchable={false}
        menuPortalTarget={PORTAL_TARGET}
        onChange={onFloorChange}
        options={floorOptions}
        placeholder={
          <Text color="secondaryText" size="small">
            {!floorOptions.length ? 'No floors available' : 'Select a floor'}
          </Text>
        }
        styles={selectStyles}
        theme={theme}
        value={floorOptions[selectedFloorIndex]}
      />
    </FloorSelectorContainer>
  );
};

const FloorSelector = withTheme(FloorSelectorInner);

// Property row component
const PropertyRow = ({ label, value }: { label: string; value: string }) => (
  <PropertyRowContainer>
    <Text color="secondaryText" size="small">
      {label}
    </Text>
    <Text color="primaryText" size="small" style={{ fontWeight: 500 }}>
      {value}
    </Text>
  </PropertyRowContainer>
);

const PureWirelessCardInner = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  theme: _theme,
}: {
  theme: DefaultTheme & { colors: Record<string, string> };
}) => {
  const dispatch = useDispatch();
  const { mapCardData } = useSelector((state: RootState) => state.mapCard);

  const properties = mapCardData?.spatialObject?.properties;
  const floors = mapCardData?.spatialObject?.floors || [];
  const selectedFloor = mapCardData?.selectedFloor;
  const estimatedCallFloor = mapCardData?.estimatedCallFloor;

  const floorOptions: FloorOption[] = useMemo(() => {
    // Sort floors by floor_level (lowest/base floor first)
    const sortedFloors = [...floors].sort(
      (a, b) => a.floor_level - b.floor_level,
    );
    return sortedFloors.map(floor => ({
      value: `${floor.floor_level}`,
      label: `Floor Level: ${floor.floor_label || floor.floor_level}`,
      isSelected: floor.floor_level === selectedFloor,
      data: floor,
    }));
  }, [floors, selectedFloor]);

  const selectedFloorIndex = useMemo(() => {
    return floorOptions.findIndex(
      floor => parseInt(floor.value, 10) === selectedFloor,
    );
  }, [floorOptions, selectedFloor]);

  const handleFloorChange = useCallback(
    (option: FloorOption) => {
      dispatch(setPureWirelessFloor(option.data.floor_level));
    },
    [dispatch],
  );

  if (!mapCardData || !properties) {
    return null;
  }

  const title =
    properties.site_name || properties.building || 'Indoor Map Details';
  const subtitle = properties.street_address
    ? `${properties.street_address}${properties.postal_community ? `, ${properties.postal_community}` : ''}${properties.zip ? ` ${properties.zip}` : ''}`
    : '';

  return (
    <SpatialLayerCardContainer>
      <CardContainer>
        {/* Header */}
        <HeaderSection>
          <Text
            color="primaryText"
            style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}
          >
            {title}
          </Text>
          <Text color="secondaryText" size="xsmall">
            Source: 911Locate
          </Text>
        </HeaderSection>

        {/* Address */}
        {subtitle && (
          <BorderedSection>
            <Text color="secondaryText" size="small" mb="2px">
              Address
            </Text>
            <Text color="primaryText" fontWeight="regular">
              {subtitle}
            </Text>
          </BorderedSection>
        )}

        {/* Estimated Caller Floor */}
        {estimatedCallFloor !== null && (
          <BorderedSection>
            <Text color="secondaryText" size="small" mb="2px">
              Estimated Caller Floor
            </Text>
            <Text color="primaryText" fontWeight="semibold">
              Floor {estimatedCallFloor}
            </Text>
          </BorderedSection>
        )}

        {/* Floor Selector */}
        {floors.length > 0 && (
          <FloorSelectorSection>
            <PropertiesContainer>
              {properties.space_type && (
                <PropertyRow label="Space Type" value={properties.space_type} />
              )}

              {properties.floor_label && (
                <PropertyRow label="Floor" value={properties.floor_label} />
              )}

              {properties.room_ID && (
                <PropertyRow label="Room ID" value={properties.room_ID} />
              )}

              {properties.wing && (
                <PropertyRow label="Wing" value={properties.wing} />
              )}

              {properties.building && (
                <PropertyRow label="Building" value={properties.building} />
              )}
            </PropertiesContainer>
            <FloorSelector
              floorOptions={floorOptions}
              selectedFloorIndex={selectedFloorIndex}
              onFloorChange={handleFloorChange}
              isDisabled={floors.length <= 1}
            />
          </FloorSelectorSection>
        )}
      </CardContainer>
    </SpatialLayerCardContainer>
  );
};

const PureWirelessCard = withTheme(PureWirelessCardInner);

export default PureWirelessCard;
